import {
  differenceInCalendarDays,
  differenceInCalendarWeeks,
  format,
  isSameDay,
} from 'date-fns';
import { PurchaseOrderItem } from '@domain/purchasing';
import { isMilestoneReached } from './milestones';
import { MilestoneKind, StatusMilestone } from './types';

export const getStatusEventLabel = (
  item: PurchaseOrderItem,
  milestone: StatusMilestone,
  today: Date
): string => {
  const day = milestone.date;
  const reached = isMilestoneReached(item, milestone.kind);
  const overdue = day < today && !reached;
  const isPresent = isSameDay(day, today);
  const isPast = day < today && reached;
  const method = item.shipment?.transportMethod;
  const methodLabel = method
    ? `${method.charAt(0).toUpperCase()}${method.slice(1)}`
    : undefined;

  const labels: Record<
    MilestoneKind,
    { overdue: string; present: string; past: string; future: string }
  > = {
    production_start: {
      overdue: 'Should have started production',
      present: 'Production starts',
      past: 'Production started',
      future: 'Production starts',
    },
    production_ready: {
      overdue: 'Should have finished production',
      present: 'Production finishes',
      past: 'Production finished',
      future: 'Production finishes',
    },
    departure: {
      overdue: methodLabel
        ? `Should have departed by ${method}`
        : 'Should have been ready to ship',
      present: methodLabel ? `${methodLabel} departs` : 'Ready to ship',
      past: methodLabel ? `${methodLabel} departed` : 'Was ready to ship',
      future: methodLabel ? `${methodLabel} departure` : 'Ready to ship',
    },
    arrival: {
      overdue: 'Should have arrived',
      present: 'Arrives',
      past: 'Arrived',
      future: 'Arrives on',
    },
    processed: {
      overdue: 'Should have been available',
      present: 'Is available',
      past: 'Became available',
      future: 'Will be available',
    },
    eta: {
      overdue: 'Should have been ready',
      present: 'Is due',
      past: 'Completed',
      future: 'ETA',
    },
  };

  const entry = labels[milestone.kind];
  if (overdue) return entry.past;
  if (isPresent) return entry.present;
  if (isPast) return entry.past;
  return entry.future;
};

export const getConfirmActionLabel = (
  item: PurchaseOrderItem,
  milestone: StatusMilestone
): string => {
  const method = item.shipment?.transportMethod;
  const methodLabel = method
    ? `${method.charAt(0).toUpperCase()}${method.slice(1)}`
    : undefined;

  switch (milestone.kind) {
    case 'production_start':
      return 'Confirm production has started';
    case 'production_ready':
      return 'Confirm production has finished';
    case 'departure':
      return methodLabel
        ? `Confirm ${methodLabel} has departed`
        : 'Confirm ready to ship';
    case 'arrival':
      return 'Confirm shipment has arrived';
    case 'processed':
    case 'eta':
      return 'Confirm is available';
  }
};

export const formatStatusDate = (date: Date, today: Date): string => {
  const days = differenceInCalendarDays(date, today);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return format(date, 'EEEE - d MMMM');
};

export const formatOverallEta = (date: Date, today: Date): string => {
  const weeks = differenceInCalendarWeeks(date, today, { weekStartsOn: 1 });
  if (weeks === 0) return 'ETA: This week';
  if (weeks === 1) return 'ETA: 1 week';
  if (weeks > 1) return `ETA: ${weeks} weeks`;
  if (weeks === -1) return 'ETA: 1 week ago';
  return `ETA: ${Math.abs(weeks)} weeks ago`;
};
